import React, { useState } from 'react'
import { Element, Tag } from 'react-bulma-components'
import { WorkersRequest } from '../../api/workers-requests'
import WorkersRequestPostDesiredWorker from './desired-worker'
import { useAssignWorkerToPost } from '../../queries/workers-requests'
import useStore from '../../store'
import { compareDocsById, getDocIdAsString } from '../../utils/documents'
import { AvailableWorker } from '../../api/users'

interface PostSuggestionsProps {
  post: WorkersRequest['posts'][0]
  workersRequest: WorkersRequest
  availableWorkers?: AvailableWorker[]
  workersWithMissionCorrespondingToPost?: AvailableWorker[]
}

const MAX_WORKERS_WITH_MISSION_DISPLAYED = 3

/**
 * Component that displays suggested workers for a post
 * Shows the list of desired workers with assignment functionality for interim agency members
 */
const PostSuggestions: React.FC<PostSuggestionsProps> = ({
  post,
  workersRequest,
  availableWorkers,
  workersWithMissionCorrespondingToPost,
}) => {
  const currentUserRole = useStore(state => state.session.currentUserRole)
  const assignWorkerToPostMutation = useAssignWorkerToPost()

  // State to manage expanded view for workers with mission correspondence
  const [showAllWorkersWithMission, setShowAllWorkersWithMission] = useState(false)

  const availableAndDesiredWorkers =
    availableWorkers?.filter(worker =>
      post.desiredWorkers.some(desiredWorker => compareDocsById(desiredWorker, worker)),
    ) || []

  // Only show suggestions if there are desired workers and no active mission
  if (!post.desiredWorkers[0] && !workersWithMissionCorrespondingToPost?.length) {
    return null
  }

  // Limit workers display to 8 initially
  const displayedWorkersWithMission = showAllWorkersWithMission
    ? workersWithMissionCorrespondingToPost
    : workersWithMissionCorrespondingToPost?.slice(0, MAX_WORKERS_WITH_MISSION_DISPLAYED)

  const remainingWorkersCount =
    (workersWithMissionCorrespondingToPost?.length || 0) - MAX_WORKERS_WITH_MISSION_DISPLAYED

  const displayDesiredWorkers = Boolean(
    availableAndDesiredWorkers.length || !workersWithMissionCorrespondingToPost?.length,
  )
  const displayWorkersWithMission = Boolean(workersWithMissionCorrespondingToPost?.length)

  return (
    <Element textSize={7} textAlign="right">
      {displayDesiredWorkers && (
        <Element>
          <Element textWeight="bold" mr={1}>
            Suggestions de l'EU :
          </Element>
          {availableAndDesiredWorkers.map(worker => (
            <Tag key={getDocIdAsString(worker)} ml={1} mb={1}>
              <WorkersRequestPostDesiredWorker
                worker={worker}
                workersRequest={workersRequest}
                post={post}
                isAvailable={availableAndDesiredWorkers.some(
                  availableWorker => availableWorker._id === worker._id,
                )}
                availableWorkers={
                  currentUserRole === 'interimAgencyMember' ? availableWorkers || [] : undefined
                }
                onClick={
                  currentUserRole === 'interimAgencyMember'
                    ? () =>
                        assignWorkerToPostMutation.mutate({
                          workersRequestId: workersRequest._id,
                          postId: post._id,
                          userId: getDocIdAsString(worker),
                        })
                    : undefined
                }
              />
            </Tag>
          ))}
        </Element>
      )}
      {displayWorkersWithMission && (
        <Element ml={1}>
          <Element textWeight="bold" mr={1}>
            Intérimaires ayant eu une mission correspondante au cours des 2 derniers mois{' '}
            {availableAndDesiredWorkers.length ? '(non suggérés par l’UE)' : ''}:
          </Element>
          {displayedWorkersWithMission?.map(worker => (
            <Tag key={worker._id} ml={1} mb={1}>
              <WorkersRequestPostDesiredWorker
                worker={worker}
                workersRequest={workersRequest}
                post={post}
                isAvailable={true}
                onClick={() => {
                  assignWorkerToPostMutation.mutate({
                    workersRequestId: workersRequest._id,
                    postId: post._id,
                    userId: worker._id,
                  })
                }}
              />
            </Tag>
          ))}
          {remainingWorkersCount > 0 && (
            <Tag
              ml={1}
              style={{ cursor: 'pointer' }}
              textColor="link"
              onClick={() => setShowAllWorkersWithMission(!showAllWorkersWithMission)}
            >
              {showAllWorkersWithMission
                ? 'Voir moins'
                : `Voir plus (${workersWithMissionCorrespondingToPost?.length || 0})...`}
            </Tag>
          )}
        </Element>
      )}
    </Element>
  )
}

export default PostSuggestions
